/**
 * WalkinRegisterPanel
 *
 * Admin → "Walk-ins" tab. Shows the WhatsApp-fed walk-in log with date
 * filter + free-text search + per-row edit/delete + a one-click Excel
 * export. Admins and managers both have access (per user spec).
 *
 * Audio playback: pending_review entries (Whisper failed in production)
 * surface the original voice note. We fetch it as an authenticated blob
 * and assign a local object URL so the <audio> element doesn't need to
 * send the JWT itself.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Download, Search, Trash2, Pencil, Users, MessageCircle, Play } from 'lucide-react';
import { api } from '../../lib/store';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WalkinAudioPlayer
 *
 * Loads the voice note as a blob (so we can attach the JWT header on the
 * fetch call) then hands the resulting object-URL to a native <audio>
 * control. Avoids any server-side query-param token gymnastics.
 */
function WalkinAudioPlayer({ walkinId }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const objectUrlRef = useRef('');

  useEffect(() => () => {
    // Revoke the blob URL when the component unmounts to free memory.
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const load = async () => {
    if (url) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('heda_token');
      const res = await axios.get(`${API_BASE}/walkins/${walkinId}/audio`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const objectUrl = URL.createObjectURL(res.data);
      objectUrlRef.current = objectUrl;
      setUrl(objectUrl);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load audio');
    } finally {
      setLoading(false);
    }
  };

  if (!url) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-1 h-7 px-2 text-[10px]"
        onClick={load}
        disabled={loading}
        data-testid={`walkin-audio-load-${walkinId}`}
      >
        <Play className="w-3 h-3 mr-1" /> {loading ? 'Loading…' : 'Play voice'}
      </Button>
    );
  }
  return <audio controls src={url} className="mt-1 h-7 w-44 block" data-testid={`walkin-audio-${walkinId}`} />;
}

/**
 * AttendingStaffEditor
 *
 * Lightweight multi-select chips for the walk-in edit dialog. Lets the
 * admin add/remove tagged staff so the WhatsApp follow-up reminder
 * fans out to every tagged user — without bloating the parent component
 * with another search/dropdown loop.
 */
function AttendingStaffEditor({ value, onChange, allStaff }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selectedIds = new Set((value || []).map((s) => s.user_id));
  const q = query.trim().toLowerCase();
  const suggestions = allStaff
    .filter((s) => !selectedIds.has(s.id))
    .filter((s) => !q ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.staff_code || '').toLowerCase().includes(q))
    .slice(0, 10);

  const add = (s) => {
    if (!s?.id) return;
    onChange([...(value || []), {
      user_id: s.id,
      name:    s.name || '',
      phone:   s.phone || '',
      role:    s.role || '',
    }]);
    setQuery('');
    setOpen(false);
  };
  const remove = (userId) => {
    onChange((value || []).filter((s) => s.user_id !== userId));
  };

  return (
    <div>
      <div
        className="mt-1 min-h-[36px] border border-[#D9DCC6] rounded-md px-2 py-1.5 flex flex-wrap gap-1 items-center bg-white"
        data-testid="walkin-edit-attending-staff"
      >
        {(value || []).map((s) => (
          <span
            key={s.user_id || s.name}
            className="inline-flex items-center gap-1 bg-[#F1F4E5] text-[#384518] text-[11px] px-2 py-0.5 rounded-full border border-[#D9DCC6]"
          >
            {s.name || 'Unnamed'}
            <button
              type="button"
              onClick={() => remove(s.user_id)}
              className="ml-0.5 text-[#8F9485] hover:text-red-600"
              aria-label={`Remove ${s.name}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={(value || []).length === 0 ? 'Add staff…' : 'Add more…'}
          className="flex-1 min-w-[100px] text-xs outline-none bg-transparent"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="relative">
          <div className="absolute z-20 mt-1 w-full bg-white border border-[#D9DCC6] rounded-md shadow-md max-h-44 overflow-y-auto">
            {suggestions.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => add(s)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F1F4E5] flex items-center justify-between"
              >
                <span>{s.name || 'Unnamed'} <span className="text-[#8F9485]">({s.role})</span></span>
                {s.staff_code && <span className="text-[10px] text-[#8F9485]">{s.staff_code}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const FIELD_LABELS = {
  customer_name:  'Customer Name',
  customer_phone: 'Phone',
  location:       'Location',
  visit_date:     'Visit Date',
  products_shown: 'Products',
  rate_quoted:    'Rate Quoted',
  quantity:       'Quantity',
  follow_up_date: 'Follow-up Date',
  follow_up_time: 'Follow-up Time (24h, e.g. 17:00)',
  architect_name: 'Architect Name',
  carpenter_name: 'Carpenter Name',
  notes:          'Notes',
};

const today = () => new Date().toISOString().slice(0, 10);

export default function WalkinRegisterPanel() {
  const [walkins, setWalkins] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Visit-date range filter (created_at "logged" range removed per business
  // request — admins only care about when the customer actually visited,
  // not when the record was back-filled into the system).
  const [visitFrom, setVisitFrom] = useState('');
  const [visitTo, setVisitTo]   = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // walkin doc OR null
  // Staff list — fetched once per mount and reused in the edit dialog's
  // multi-select. Keeping it at the panel level avoids re-fetching on
  // every edit click.
  const [allStaff, setAllStaff] = useState([]);

  useEffect(() => {
    api.getSalespeople()
      .then((res) => setAllStaff(res.data?.salespeople || []))
      .catch(() => setAllStaff([]));
  }, []);

  const params = () => {
    const p = {};
    if (visitFrom) p.visit_date_from = visitFrom;
    if (visitTo) p.visit_date_to = visitTo;
    if (search.trim()) p.search = search.trim();
    return p;
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.listWalkins(params());
      setWalkins(res.data?.walkins || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load walk-ins');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh on date/search debounce — but only refresh on enter or
  // blur for the search input to avoid spamming.
  // Re-fetch when filters change. queueMicrotask defers state setters to
  // satisfy `react-hooks/set-state-in-effect` on React 19.
  useEffect(() => {
    let cancelled = false;
     
    queueMicrotask(() => { if (!cancelled) refresh(); });
    return () => { cancelled = true; };
  }, [visitFrom, visitTo]);  // eslint-disable-line react-hooks/exhaustive-deps

  const exportXlsx = async () => {
    try {
      const res = await api.exportWalkinsXlsx(params());
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `walkin_register_${today()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Excel export failed');
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete walk-in for "${w.customer_name || 'this customer'}"? This cannot be undone.`)) return;
    try {
      await api.deleteWalkin(w.id);
      toast.success('Walk-in deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-walkin-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#556B2F]" />
          <h2 className="text-base font-bold text-[#1A1C16]">Walk-in Register</h2>
          <span className="text-[10px] bg-[#25D366]/10 border border-[#25D366]/40 text-[#1B7531] px-2 py-0.5 rounded-full">
            <MessageCircle className="w-3 h-3 inline-block mr-1" /> WhatsApp fed
          </span>
        </div>
        <Button
          onClick={exportXlsx}
          className="bg-[#556B2F] hover:bg-[#445524] text-white"
          data-testid="walkin-export-btn"
        >
          <Download className="w-4 h-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-[#E2E6D9]">
        <CardContent className="pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-[#5C6150]">Visit From</Label>
            <Input type="date" value={visitFrom} onChange={e => setVisitFrom(e.target.value)} className="mt-1" data-testid="walkin-filter-visit-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-[#5C6150]">Visit To</Label>
            <Input type="date" value={visitTo} onChange={e => setVisitTo(e.target.value)} className="mt-1" data-testid="walkin-filter-visit-to" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase text-[#5C6150]">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#8F9485]" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') refresh(); }}
                onBlur={refresh}
                placeholder="Customer, phone, product, salesperson, location, staff, notes…"
                className="pl-8"
                data-testid="walkin-filter-search"
              />
            </div>
          </div>
          {(visitFrom || visitTo) && (
            <div className="col-span-2 sm:col-span-4 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setVisitFrom(''); setVisitTo(''); }}
                className="text-[10px] h-7"
                data-testid="walkin-filter-clear-visit"
              >
                Clear Visit-Date Filter
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      <Card className="border-[#E2E6D9]">
        <CardContent className="pt-4">
          {loading ? (
            <p className="text-sm text-[#8F9485] py-6 text-center">Loading…</p>
          ) : walkins.length === 0 ? (
            <p className="text-sm text-[#8F9485] py-6 text-center">No walk-ins match the current filters.</p>
          ) : (
            <>
              <p className="text-xs text-[#5C6150] mb-2"><span className="font-semibold">{total}</span> walk-in{total === 1 ? '' : 's'} found</p>
              {(() => {
                // Compute per-render which of Rate / Qty / Architect
                // columns have ANY value in the current result set —
                // entirely-empty columns are hidden (header + all
                // cells) so the table doesn't waste horizontal space.
                const showRate = walkins.some((w) => (w.rate_quoted || '').toString().trim() !== '');
                const showQty  = walkins.some((w) => (w.quantity || '').toString().trim() !== '');
                const showArch = walkins.some((w) => (w.architect_name || '').toString().trim() !== '');
                return (
              <div className="border border-[#E2E6D9] rounded-md overflow-x-auto">
                <table className="w-full text-xs min-w-[920px]">
                  <thead className="bg-[#F2F4EF] text-[#5C6150]">
                    <tr>
                      <th className="text-left py-2 px-3">Date / Time</th>
                      <th className="text-left py-2 px-3">Salesperson</th>
                      <th className="text-left py-2 px-3">Attended By</th>
                      <th className="text-left py-2 px-3">Customer</th>
                      <th className="text-left py-2 px-3">Phone</th>
                      <th className="text-left py-2 px-3">Visit Date</th>
                      <th className="text-left py-2 px-3">Location</th>
                      <th className="text-left py-2 px-3">Products</th>
                      {showRate && <th className="text-left py-2 px-3">Rate</th>}
                      {showQty  && <th className="text-left py-2 px-3">Qty</th>}
                      {showArch && <th className="text-left py-2 px-3">Architect</th>}
                      <th className="text-left py-2 px-3">Carpenter</th>
                      <th className="text-left py-2 px-3">Follow-up</th>
                      <th className="text-right py-2 px-3 w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walkins.map(w => {
                      const isPending = w.status === 'pending_review';
                      return (
                      <tr
                        key={w.id}
                        className={`border-t border-[#E2E6D9] hover:bg-[#FAFBF5] ${isPending ? 'bg-[#FFF8E1]' : ''}`}
                        data-testid={`walkin-row-${w.id}`}
                      >
                        <td className="py-2 px-3 font-mono text-[10px] whitespace-nowrap">
                          {(w.created_at || '').replace('T',' ').slice(0,16)}
                          {isPending && (
                            <span className="block text-[9px] uppercase tracking-wide text-[#B07700] font-bold mt-0.5">
                              ⚠ Pending review
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <span className="font-medium">{w.salesperson_name || '—'}</span>
                          {w.salesperson_staff_code && <span className="ml-1 text-[9px] text-[#556B2F]">[{w.salesperson_staff_code}]</span>}
                          {w.audio_filename && <WalkinAudioPlayer walkinId={w.id} />}
                        </td>
                        <td className="py-2 px-3" data-testid={`walkin-attending-staff-${w.id}`}>
                          {Array.isArray(w.attending_staff) && w.attending_staff.length > 0 ? (() => {
                            // Show up to 2 pills inline; anything beyond
                            // collapses into a single "+N" pill whose
                            // title lists every remaining name so hover
                            // reveals the full attendee list.
                            const shown = w.attending_staff.slice(0, 2);
                            const rest  = w.attending_staff.slice(2);
                            const fullList = w.attending_staff.map((s) => s.name || 'Unnamed').join(', ');
                            return (
                              <div className="flex flex-wrap gap-1 max-w-[170px]" title={fullList}>
                                {shown.map((s) => (
                                  <span
                                    key={s.user_id || s.name}
                                    className="inline-block bg-[#F1F4E5] text-[#384518] text-[10px] px-1.5 py-0.5 rounded-full border border-[#D9DCC6]"
                                    title={s.phone || ''}
                                  >
                                    {s.name || 'Unnamed'}
                                  </span>
                                ))}
                                {rest.length > 0 && (
                                  <span
                                    className="inline-block bg-[#E2E6D9] text-[#384518] text-[10px] px-1.5 py-0.5 rounded-full border border-[#D9DCC6] font-semibold"
                                    title={rest.map((s) => s.name || 'Unnamed').join(', ')}
                                  >
                                    +{rest.length}
                                  </span>
                                )}
                              </div>
                            );
                          })() : (
                            <span className="text-[#8F9485]">—</span>
                          )}
                        </td>
                        <td className="py-2 px-3 font-medium">{w.customer_name || '—'}</td>
                        <td className="py-2 px-3 font-mono">{w.customer_phone || '—'}</td>
                        <td className="py-2 px-3 whitespace-nowrap text-[#5C6150] font-mono-data" data-testid={`walkin-visit-date-${w.id}`}>{w.visit_date || (w.created_at ? String(w.created_at).slice(0, 10) : '—')}</td>
                        <td className="py-2 px-3 whitespace-nowrap text-[#5C6150]" data-testid={`walkin-location-${w.id}`}>{w.location || '—'}</td>
                        <td className="py-2 px-3 max-w-[200px] truncate" title={w.products_shown}>{w.products_shown || '—'}</td>
                        {showRate && <td className="py-2 px-3 whitespace-nowrap">{w.rate_quoted || '—'}</td>}
                        {showQty  && <td className="py-2 px-3 whitespace-nowrap">{w.quantity || '—'}</td>}
                        {showArch && <td className="py-2 px-3 whitespace-nowrap text-[#5C6150]" data-testid={`walkin-architect-${w.id}`}>{w.architect_name || '—'}</td>}
                        <td className="py-2 px-3 whitespace-nowrap text-[#5C6150]" data-testid={`walkin-carpenter-${w.id}`}>{w.carpenter_name || '—'}</td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          {w.follow_up_date || '—'}
                          {w.follow_up_time && <span className="ml-1 text-[10px] text-[#5C6150]">@ {w.follow_up_time}</span>}
                          {w.follow_up_reminder_sent && (
                            <span
                              className="ml-1 inline-flex items-center text-[9px] bg-[#25D366]/10 text-[#1B7531] rounded-full px-1.5 py-0.5"
                              title={w.reminder_sent_at ? `Reminder sent ${w.reminder_sent_at.slice(0,16).replace('T',' ')} IST` : 'Reminder sent'}
                            >
                              🔔 sent
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(w)} title={isPending ? 'Listen + fill details' : 'Edit'} data-testid={`walkin-edit-${w.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-[#556B2F]" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => remove(w)} title="Delete" data-testid={`walkin-delete-${w.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              ); })()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="walkin-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit walk-in</DialogTitle>
            <DialogDescription className="text-xs text-[#5C6150]">
              Logged via {editing?.source || 'whatsapp'} on {(editing?.created_at || '').replace('T',' ').slice(0,16)}.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {editing.audio_filename && (
                <div className="bg-[#FFF8E1] border border-[#E2D9A0] rounded p-2">
                  <Label className="text-[10px] uppercase text-[#5C6150]">Original Voice Note</Label>
                  <WalkinAudioPlayer walkinId={editing.id} />
                  {editing.failure_reason && (
                    <p className="text-[10px] text-[#B07700] mt-1">
                      Auto-transcription failed: <code>{editing.failure_reason}</code>
                    </p>
                  )}
                </div>
              )}
              {Object.entries(FIELD_LABELS).map(([key, label]) => (
                <div key={key}>
                  <Label className="text-[10px] uppercase text-[#5C6150]">{label}</Label>
                  {key === 'notes' ? (
                    <Textarea
                      value={editing[key] || ''}
                      onChange={e => setEditing({ ...editing, [key]: e.target.value })}
                      rows={3}
                      className="mt-1 text-sm"
                      data-testid={`walkin-edit-${key}`}
                    />
                  ) : key === 'follow_up_time' ? (
                    <Input
                      value={editing[key] || ''}
                      onChange={e => setEditing({ ...editing, [key]: e.target.value })}
                      className="mt-1 text-sm font-mono-data"
                      type="time"
                      data-testid={`walkin-edit-${key}`}
                    />
                  ) : (
                    <Input
                      value={editing[key] || ''}
                      onChange={e => setEditing({ ...editing, [key]: e.target.value })}
                      className="mt-1 text-sm"
                      type={key === 'follow_up_date' ? 'date' : 'text'}
                      data-testid={`walkin-edit-${key}`}
                    />
                  )}
                </div>
              ))}
              <div>
                <Label className="text-[10px] uppercase text-[#5C6150]">Attended By</Label>
                <AttendingStaffEditor
                  value={editing.attending_staff || []}
                  onChange={(arr) => setEditing({ ...editing, attending_staff: arr })}
                  allStaff={allStaff}
                />
                <p className="text-[10px] text-[#8F9485] mt-1">
                  Each tagged staff gets the WhatsApp follow-up reminder on the follow-up date.
                </p>
              </div>
              {editing.transcript && (
                <div>
                  <Label className="text-[10px] uppercase text-[#5C6150]">Original transcript</Label>
                  <p className="mt-1 text-[11px] italic text-[#8F9485] bg-[#FAFBF5] border border-[#E2E6D9] rounded p-2 whitespace-pre-wrap">
                    {editing.transcript}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E6D9]">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button
                  className="bg-[#556B2F] hover:bg-[#445524] text-white"
                  onClick={async () => {
                    try {
                      await api.updateWalkin(editing.id, {
                        customer_name:  editing.customer_name  || '',
                        customer_phone: editing.customer_phone || '',
                        location:       editing.location       || '',
                        visit_date:     editing.visit_date     || '',
                        products_shown: editing.products_shown || '',
                        rate_quoted:    editing.rate_quoted    || '',
                        quantity:       editing.quantity       || '',
                        follow_up_date: editing.follow_up_date || '',
                        follow_up_time: editing.follow_up_time || '',
                        architect_name: editing.architect_name || '',
                        carpenter_name: editing.carpenter_name || '',
                        notes:          editing.notes          || '',
                        attending_staff: editing.attending_staff || [],
                      });
                      toast.success('Walk-in updated');
                      setEditing(null);
                      refresh();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Update failed');
                    }
                  }}
                  data-testid="walkin-edit-save"
                >Save changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
